import React, { useState, useRef } from 'react';
import { Box, Card, CardContent, Typography, Button, Grid, Avatar, IconButton, CircularProgress,  } from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Save as SaveIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { userInfoState } from '../../recoil/atoms';
import client from '../../api/client';
import CustomTextField from '../../components/CustomTextField';

const Profile = () => {
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    shop_name: userInfo?.shop_name || '',
    phone: userInfo?.phone || '',
    address: userInfo?.address || '',
  });

  // آپلود عکس
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // چک سایز (حداکثر 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم عکس نباید بیشتر از ۵ مگابایت باشد');
      return;
    }

    // چک نوع فایل
    if (!file.type.startsWith('image/')) {
      toast.error('لطفاً فقط فایل تصویری انتخاب کنید');
      return;
    }

    // پیش‌نمایش
    const reader = new FileReader();
    reader.onload = (e) => setPreviewImage(e.target.result);
    reader.readAsDataURL(file);

    // آپلود
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await client.post('/users/shop/upload-image/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // آپدیت userInfo
      const updatedUser = { ...userInfo, shop_image: response.data.image_url };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUserInfo(updatedUser);

      toast.success('عکس با موفقیت آپلود شد');
    } catch (error) {
      toast.error('خطا در آپلود عکس');
      setPreviewImage(null);
    } finally {
      setUploading(false);
    }
  };

  // ذخیره مشخصات
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.shop_name.trim()) {
      toast.error('نام فروشگاه الزامی است');
      return;
    }

    setLoading(true);
    try {
      const response = await client.put('/users/shop/profile/', formData);

      // آپدیت userInfo
      const updatedUser = { 
        ...userInfo, 
        shop_name: formData.shop_name,
        phone: formData.phone,
        address: formData.address,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUserInfo(updatedUser);

      toast.success('مشخصات با موفقیت ذخیره شد');
    } catch (error) {
      toast.error('خطا در ذخیره مشخصات');
    } finally {
      setLoading(false);
    }
  };

  const currentImage = previewImage || 
    (userInfo?.shop_image ? `${process.env.REACT_APP_API_URL || ''}${userInfo.shop_image}` : null);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
        مشخصات فروشگاه
      </Typography>

      {/* آپلود عکس */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            عکس فروشگاه
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            این عکس به عنوان پس‌زمینه داشبورد شما نمایش داده می‌شود
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* پیش‌نمایش */}
            <Box
              sx={{
                width: { xs: '100%', sm: 200 },
                height: 150,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {currentImage ? (
                <Box
                  component="img"
                  src={currentImage}
                  alt="عکس فروشگاه"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <StoreIcon sx={{ fontSize: 60, color: 'grey.400' }} />
              )}

              {uploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress sx={{ color: '#fff' }} />
                </Box>
              )}
            </Box>

            {/* دکمه انتخاب */}
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                انتخاب عکس
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                حداکثر ۵ مگابایت - JPG, PNG
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* فرم مشخصات */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            اطلاعات فروشگاه
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomTextField                   label="نام فروشگاه *"
                  value={formData.shop_name}
                  onChange={(e) => setFormData({ ...formData, shop_name: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField                   label="شماره موبایل"
                  value={userInfo?.mobile || userInfo?.username || ''}
                  disabled
                  fullWidth
                  helperText="برای تغییر موبایل با پشتیبانی تماس بگیرید"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField                   label="تلفن ثابت"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <CustomTextField                   label="آدرس"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  sx={{ minWidth: 150 }}
                >
                  ذخیره تغییرات
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;